'use client';
import { cn } from '@/lib/utils';
import { StrapiAchievement, StrapiPostTag } from '@/types/strapi';
import parse from 'html-react-parser';
import { ChevronDown } from 'lucide-react';
import { useTranslations } from 'next-intl';
import { useState } from 'react';
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger
} from '../ui/accordion';

export default function Achievement(Prop: {
  slug?: string;
  moduleOption: any;
  moduleData: {
    awardGroups: StrapiPostTag[];
    achievements: StrapiAchievement[];
  };
}) {
  const {moduleOption} = Prop;
  const t = useTranslations('achievementPage');

  const achievementData = Prop.moduleData;
  const awardGroupMap = (achievementData.awardGroups || []).reduce(
    (acc: {[key: string]: string}, curr) => {
      acc[curr.slug] = curr.title;
      return acc;
    },
    {}
  );

  // default sequence
  const seq: string[] = moduleOption?.sequence || [
    'as-watson-group',
    'as-watson-industries',
    'watsons-asia',
    'watsons-mainland-china',
    'watsons-hong-kong',
    'watsons-taiwan',
    'watsons-singapore',
    'watsons-thailand',
    'watsons-malaysia',
    'watsons-philippines',
    'watsons-indonesia',
    'watsons-turkey',
    'parknshop-mainland-china',
    'parknshop-hong-kong',
    'fusion-hong-kong',
    'taste-mainland-china',
    'taste-hong-kong',
    'gourmet',
    'great',
    'fortress',
    'watsons-wine',
    'watsons-water',
    'mr-juicy',
    'watsons-ukraine',
    'kruidvat-netherlands',
    'kruidvat-belgium',
    'trekpleister',
    'rossmann',
    'superdrug',
    'savers',
    'drogas-latvia',
    'iciparisxl-belgium',
    'iciparisxl-netherlands',
    'iciparisxl-luxembourg',
    'the-perfume-shop'
  ];

  // sort achievements by sequence
  const achievements = (achievementData.achievements || []).sort((a, b) => {
    const seq_a = seq.indexOf(a.slug);
    const seq_b = seq.indexOf(b.slug);
    if (seq_a >= 0 && seq_b >= 0) {
      return seq_a < seq_b ? -1 : 1;
    }
    if (seq_a >= 0) {
      return -1;
    }
    if (seq_b >= 0) {
      return 1;
    }
    return 0;
  });

  const defaultGroupColors = {
    'awards-group': 'bg-[#E21736]',
    'awards-asia': 'bg-[#139cd3]',
    'awards-europe': 'bg-[#ECBC2B]'
  };
  const groupColors = {
    ...(moduleOption?.group?.colors || {}),
    ...defaultGroupColors
  };
  const groupSeq =
    moduleOption?.group?.sequence || Object.keys(defaultGroupColors);
  const achievementGroupItems: {
    [key: string]: {
      title: string;
      bgColor: string;
      items: StrapiAchievement[];
    };
  } = {
    'awards-group': {
      title: awardGroupMap['awards-group'],
      bgColor: groupColors['awards-group'],
      items: []
    },
    'awards-asia': {
      title: awardGroupMap['awards-asia'],
      bgColor: groupColors['awards-asia'],
      items: []
    },
    'awards-europe': {
      title: awardGroupMap['awards-europe'],
      bgColor: groupColors['awards-europe'],
      items: []
    }
  };

  achievements.map((achievement: StrapiAchievement) => {
    const awardGroup = achievement.award_group;
    achievementGroupItems[awardGroup].items.push(achievement);
  });

  const [expanded, setExpanded] = useState<{
    [key: string]: boolean;
  }>({});

  return (
    <div className="questrial text-[18px] flex flex-col gap-4">
      {groupSeq.map((awardGroupKey: string) => {
        if (achievementGroupItems[awardGroupKey]) {
          return (
            <div key={awardGroupKey} className="h-full" id={awardGroupKey}>
              <Accordion type="single" collapsible defaultValue="expanded">
                <AccordionItem value="expanded">
                  <AccordionTrigger
                    className={cn(
                      'p-4 text-white',
                      achievementGroupItems[awardGroupKey].bgColor
                    )}
                  >
                    <h2 className={cn('text-2xl font-[700] text-white')}>
                      {achievementGroupItems[awardGroupKey].title}
                    </h2>
                    <ChevronDown
                      className={cn(
                        'h-4 w-4 shrink-0 text-muted-foreground transition-transform duration-200 text-white'
                      )}
                    />
                  </AccordionTrigger>
                  <AccordionContent>
                    <div
                      className={cn(
                        'transition-all duration-300 ease-in-out transform origin-top '
                      )}
                    >
                      <div className="grid grid-cols-1 gap-10 ">
                        {achievementGroupItems[awardGroupKey].items.map(
                          (achievement, index) => {
                            return (
                              <div key={index} id={achievement.slug}>
                                {!achievement?.hide_logo && (
                                  <div className="flex flex-col my-4">
                                    {achievement.image && (
                                      <img
                                        src={achievement.image?.url}
                                        alt={achievement.title}
                                        width={200}
                                        height={60}
                                        className=""
                                      />
                                    )}
                                  </div>
                                )}
                                {!achievement?.hide_title && (
                                  <h3 className="text-sm font-[700] mt-2 text-[#e21736]">
                                    {achievement.title}
                                  </h3>
                                )}
                                <div
                                  className={cn(
                                    'flex flex-col gap-4 bg-[#ECECEC] transition-all duration-300 ease-in-out transform origin-top ',
                                    ' scrollbar-thin scrollbar-thumb-rounded-full ',
                                    'md:max-h-[40vh] md:overflow-auto',
                                    // 'md:overflow-hidden md:hover:overflow-auto md:max-h-[40vh]',
                                    expanded[achievement.slug]
                                      ? 'overflow-auto max-h-[70vh]'
                                      : 'overflow-hidden max-h-[40vh]'
                                  )}
                                >
                                  {achievement?.awards?.map(
                                    (
                                      award: {
                                        year: string;
                                        title: string;
                                        content: string;
                                      },
                                      index: number
                                    ) => {
                                      return (
                                        <div
                                          key={index}
                                          className="grid grid-cols-5 justify-between items-start p-4 "
                                        >
                                          <div className="text-[18px]">
                                            {' '}
                                            {award.year}
                                          </div>
                                          <div className="col-span-4">
                                            <h4
                                              className={cn(
                                                'text-lg font-[700]'
                                              )}
                                            >
                                              {parse(award.title)}
                                            </h4>
                                            <p className={cn('text-base')}>
                                              {award.content &&
                                                parse(award.content)}
                                            </p>
                                          </div>
                                        </div>
                                      );
                                    }
                                  )}
                                </div>
                                <div
                                  className="md:hidden m-auto w-fit"
                                  onClick={() => {
                                    setExpanded({
                                      ...expanded,
                                      [achievement.slug]:
                                        !expanded[achievement.slug]
                                    });

                                    const pagination = document.getElementById(
                                      achievement.slug
                                    );
                                    if (pagination) {
                                      const top =
                                        pagination.getBoundingClientRect().top +
                                        window.scrollY -
                                        20;
                                      window.scrollTo({
                                        top: top,
                                        behavior: 'smooth'
                                      });
                                    }
                                  }}
                                >
                                  {!expanded[achievement.slug] && (
                                    <div className="flex justify-center items-center my-4 text-sm">
                                      <span className="material-symbols outlined">
                                        keyboard_arrow_down
                                      </span>
                                      {t('more')}
                                    </div>
                                  )}
                                  {expanded[achievement.slug] && (
                                    <div className="flex justify-center items-center my-4 text-sm">
                                      <span className="material-symbols outlined">
                                        keyboard_arrow_up
                                      </span>
                                      {t('less')}
                                    </div>
                                  )}
                                </div>
                              </div>
                            );
                          }
                        )}
                      </div>
                    </div>
                  </AccordionContent>
                </AccordionItem>
              </Accordion>
            </div>
          );
        }
      })}
    </div>
  );
}
